import { useLocale } from '@app/core/hooks/useLocale';
import TableSectionValuesPerForecast from '../../components/TableSectionValuesPerForecast';
import CellValue from '@app/shared/components/cell-value/CellValue';
import {
  cellValueFormattingRules,
  amountRules,
} from '@app/shared/components/cell-value/CellValueConfigurations';
import { TransactionType } from '@app/shared/models/contracts/enums/shared-enums';
import { Pwerm2CalculationWaterfallCaseYearInstrumentMovementTransactionDto } from '@app/shared/models/contracts/pwerm2-calculation-results-dto';
import { FC } from 'react';

type ValueSelector<T> = (item: T) => number;

interface InterimsByTypeSectionProps {
  transactionsByCaseAndYear: Record<
    string,
    Record<string, Pwerm2CalculationWaterfallCaseYearInstrumentMovementTransactionDto[]>
  >;
  transactionType: TransactionType;
  headerLabel: string;
  valueSelector: ValueSelector<Pwerm2CalculationWaterfallCaseYearInstrumentMovementTransactionDto>;
  cellValueFormattingRules: cellValueFormattingRules;
  colCount: number;
}

export const InterimsByTypeSection: FC<InterimsByTypeSectionProps> = ({
  transactionsByCaseAndYear,
  transactionType,
  headerLabel,
  valueSelector,
  cellValueFormattingRules,
  colCount,
}) => {
  const { l } = useLocale();

  const maxNumberOfTransactionsInYear = Math.max(
    0,
    ...Object.values(transactionsByCaseAndYear)
      .flatMap((t) => Object.values(t))
      .map((t) => t.length)
  );

  if (!maxNumberOfTransactionsInYear) {
    return null;
  }

  const transactions = Object.values(transactionsByCaseAndYear).flatMap((cases) =>
    Object.values(cases).flat()
  );

  const eventNarratives = (() => {
    const eventNarrativeDates = transactions.reduce<Map<string, string>>(
      (acc, { eventNarrative, date }) => {
        if (!eventNarrative) {
          return acc;
        }
        const existing = acc.get(eventNarrative);
        if (!existing || new Date(date) < new Date(existing)) {
          acc.set(eventNarrative, date);
        }
        return acc;
      },
      new Map<string, string>()
    );

    return Array.from(eventNarrativeDates.keys()).sort((a, b) => {
      const dateA = new Date(eventNarrativeDates.get(a) as string).getTime();
      const dateB = new Date(eventNarrativeDates.get(b) as string).getTime();
      return dateA !== dateB ? dateA - dateB : a.localeCompare(b);
    });
  })();

  return (
    <>
      <tr data-testid="sponsor-mom-irr-header-row">
        <th className="table-primary__cell--header-tertiary">{headerLabel}</th>
        <th colSpan={colCount - 1} className="table-primary__cell--header-tertiary" />
      </tr>

      {eventNarratives.map((eventNarrative, index) => (
        <TableSectionValuesPerForecast
          key={`${transactionType}-${index}`}
          title={eventNarrative}
          renderSpacer={false}
          renderCell={({ forecast, caseItem }) => {
            const eventTransactions = transactionsByCaseAndYear[caseItem.caseId]?.[
              forecast.forecastYear
            ]?.filter((t) => t.eventNarrative === eventNarrative);

            const total =
              eventTransactions?.length > 0
                ? eventTransactions.reduce(
                    (acc, transaction) => acc + valueSelector(transaction),
                    0
                  )
                : null;

            return (
              <CellValue
                {...cellValueFormattingRules}
                value={total ?? null}
                dataTestId={`${transactionType}-cell-${caseItem.caseId}-${forecast.forecastId}-${index}`}
              />
            );
          }}
        />
      ))}
      <TableSectionValuesPerForecast
        title={l('_Total')}
        additionalTitleClassNames="table-primary__cell--strong"
        renderSpacer={false}
        renderCell={({ forecast, caseItem }) => {
          const totalValue = (
            (transactionsByCaseAndYear[caseItem.caseId] ?? {})[forecast.forecastYear] ?? []
          ).reduce((acc, transaction) => acc + valueSelector(transaction), 0);

          return (
            <CellValue
              {...amountRules}
              className="table-primary__cell--strong"
              value={totalValue !== 0 ? totalValue : null}
              dataTestId={`total-${transactionType}-cell-${caseItem.caseId}-${forecast.forecastId}`}
            />
          );
        }}
      />
    </>
  );
};
